/**
 * PLAT-11050 — the reel's stored shape (DE12).
 *
 * Only what the timeline cannot supply lives here: names, where they came
 * from, chapter titles and chapter boundaries. Order, membership and contents
 * are all read off the timeline (DE3), so there is no second ordering model to
 * drift from it.
 */

import type { ReelSectionOrigin } from '@src/types.ts/story';

/**
 * Where a name came from — it decides what a re-generation may overwrite
 * (DE10). A name a creator typed is never clobbered by a later generation.
 */
export type NameSource = 'clip' | 'generated' | 'user';

export type ReelSectionName = {
  name: string;
  nameSource: NameSource;
};

export type ReelChapter = {
  id: string;
  title: string;
  titleSource: NameSource;
  collapsed?: boolean;
  /** The Section this chapter starts at; it runs to the next chapter's. */
  firstSectionId: string;
  /**
   * Set in place of `firstSectionId` (left `''`) for a chapter that owns no
   * Sections: it sits in the gap after this one, and starts at whichever
   * Section lands there next. `''` is the gap at the very top of the reel, the
   * one with no Section before it (PLAT-11075 DS4).
   *
   * An empty chapter is representable anywhere the anchor can point, not only
   * past the last Section: what distinguishes it from the chapter that owns the
   * Section below the same gap is that it owns nothing (PLAT-11075 DS3).
   */
  afterSectionId?: string;
};

/**
 * PLAT-11072 — a Section the creator has turned off (DS1/DS2).
 *
 * An off Section is taken **out of the timeline**, so playback, duration,
 * `subClips` and rendering are all correct without knowing the feature exists.
 * What that costs is the passage itself — which is why this entry holds
 * everything it takes to put it back, and the few things its row shows while it
 * is away (DS11). Precisely DE13's rule: the timeline cannot supply any of it,
 * because the passage is no longer there.
 */
export type DisabledReelSection = {
  /** The Section's provenance id as it was; a re-insert mints a new one. */
  id: string;
  /** The passage in source-media time — what turning it back on rebuilds from. */
  range: { start: number; end: number };
  name?: string;
  nameSource?: NameSource;
  /** PLAT-11067 lineage, kept so it survives the round trip. */
  origin?: ReelSectionOrigin[];
  /**
   * The row this one sits after; `''` opens the reel (DS3). The row holds its
   * place in the list this way, having no timeline position to hold one for it.
   */
  afterSectionId?: string;
  /**
   * What the row shows. Every other row reads these off its elements; this one
   * has none on the timeline, so the three of them travel with the entry
   * (DS11).
   */
  openingWords: string;
  firstLine: string;
  speakerLabels: string[];
};

export type Reel = {
  chapters: ReelChapter[];
  /** Section names + provenance, keyed by Section id. */
  sections: Record<string, ReelSectionName>;
  /**
   * PLAT-11072 — the Sections that are off, in no particular order: each one
   * says where it sits (DS3), so the list itself carries no ordering. Absent
   * rather than empty, so a reel with nothing off stores nothing.
   */
  disabledSections?: DisabledReelSection[];
};

export const EMPTY_REEL: Reel = { chapters: [], sections: {} };
